import { useEffect, useState } from "react";

type UseEmptyStateVisibilityArgs = {
  isEmpty: boolean;
  isLoading?: boolean;
  delayMs?: number;
};

/**
 * If loading is explicitly provided, empty state becomes fully state-driven.
 * Otherwise we keep a small delayed fallback for legacy call sites.
 */
export function useEmptyStateVisibility({
  isEmpty,
  isLoading,
  delayMs = 450,
}: UseEmptyStateVisibilityArgs) {
  const hasExplicitLoading = typeof isLoading === "boolean";
  const [showDelayedEmpty, setShowDelayedEmpty] = useState(false);

  useEffect(() => {
    if (hasExplicitLoading) {
      setShowDelayedEmpty(false);
      return;
    }
    if (!isEmpty) {
      setShowDelayedEmpty(false);
      return;
    }
    if (delayMs <= 0) {
      setShowDelayedEmpty(true);
      return;
    }

    const timer = setTimeout(() => setShowDelayedEmpty(true), delayMs);
    return () => clearTimeout(timer);
  }, [isEmpty, delayMs, hasExplicitLoading]);

  if (hasExplicitLoading) {
    return isEmpty && !isLoading;
  }

  return isEmpty && showDelayedEmpty;
}
